import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ServiceLayout from "@/components/ServiceLayout";
import PhaseHero from "@/components/how-it-works/PhaseHero";
import StrategySection from "@/components/strategy/StrategySection";
import { BarChart3, Brain } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import PerformanceShowcase from "@/components/performance/PerformanceShowcase";

const serviceImg = "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/services-heroes/Graphics-26.png";

const strategySections = [
  {
    title: "Performance Systems Design & Deployment",
    description: "Design and deploy KPI tracking systems, real-time dashboards, and data models that connect operations, finance, and outcomes.",
    processes: [
      { id: 1, text: "KPI tracking systems aligned with operational reality" },
      { id: 2, text: "Real-time dashboards and performance reporting" },
      { id: 3, text: "Data models connecting operations, finance, and outcomes" },
      { id: 4, text: "Performance baseline establishment and benchmarking" },
      { id: 5, text: "Automated alerting and exception management" }
    ],
    outcomes: [
      { id: 1, text: "Real-time performance visibility across key dimensions" },
      { id: 2, text: "Data-driven culture with clear ownership and accountability" },
      { id: 3, text: "Measurable performance baselines for continuous improvement" }
    ],
    timeframe: "6 - 10 WEEKS"
  },
  {
    title: "Governance & Decision-Making Routines",
    description: "Establish structured governance processes, decision-making frameworks, and cross-functional performance alignment.",
    processes: [
      { id: 1, text: "Weekly and monthly performance review cadences" },
      { id: 2, text: "Structured decision-making frameworks" },
      { id: 3, text: "Cross-functional performance alignment (Operations, Finance, Commercial)" },
      { id: 4, text: "Escalation protocols and accountability mapping" },
      { id: 5, text: "Governance documentation and knowledge management" }
    ],
    outcomes: [
      { id: 1, text: "Structured governance and decision-making processes" },
      { id: 2, text: "Clear ownership, roles, and accountability" },
      { id: 3, text: "Aligned cross-functional performance routines" }
    ],
    timeframe: "4 - 8 WEEKS"
  },
  {
    title: "Advanced Technology Integration",
    description: "Integrate real-time analytics, predictive models, and IoT data streams into unified performance management systems.",
    processes: [
      { id: 1, text: "Real-time analytics and monitoring systems" },
      { id: 2, text: "Predictive and performance optimization models" },
      { id: 3, text: "Integration with IoT and operational data streams" },
      { id: 4, text: "Unified data architecture for performance intelligence" },
      { id: 5, text: "Advanced visualization and reporting layers" }
    ],
    outcomes: [
      { id: 1, text: "Integrated dashboards and reporting systems" },
      { id: 2, text: "Predictive insights for proactive decision-making" },
      { id: 3, text: "Connected operational and financial data streams" }
    ],
    timeframe: "8 - 12 WEEKS"
  },
  {
    title: "Smart Contract Performance Layers",
    description: "Implement blockchain-based performance tracking, automated SLA/KPI execution, and transparent audit systems.",
    processes: [
      { id: 1, text: "Smart contract-based performance tracking and validation" },
      { id: 2, text: "Automated execution of performance-based agreements (SLAs, KPIs, payments)" },
      { id: 3, text: "Transparent, auditable performance records" },
      { id: 4, text: "Tokenized incentive and outcome-based mechanisms" },
      { id: 5, text: "On-chain governance and compliance frameworks" }
    ],
    outcomes: [
      { id: 1, text: "Smart contract-enabled performance accountability" },
      { id: 2, text: "Automated, transparent performance enforcement" },
      { id: 3, text: "Tokenized incentive structures for outcome alignment" }
    ],
    timeframe: "7 - 11 WEEKS"
  }
];

const AIAssistants = () => {
  useDocumentTitle(
    "Performance Management — Transformation | Klexu",
    "Continuous performance management for industrial AI: KPI tracking, governance, and outcome verification for performance-based contracts."
  );

  return (
    <>
      <Navbar />
      <PhaseHero
        number="04"
        eyebrow="Performance Management "
        titleLead="Sustain what you"
        titleHighlight="built."
        body="Most transformation programs end at launch. The Performance Management Phase doesn't. It provides ongoing governance — monthly KPI reviews, contract compliance monitoring, supplier performance audits, and continuous optimisation. The performance-based model compounds over time. This is how it compounds."
        whatYouGet="Monthly performance reporting. Supplier accountability governance. Continuous improvement recommendations. Quarterly strategic reviews."
        whoItsFor="Organisations that have deployed performance-based models and need structured governance to sustain and scale them."
        ctaLabel="Plan ahead"
        image={serviceImg}
      />
      <ServiceLayout title="Performance Management" description="" icon={<BarChart3 className="h-8 w-8 text-accent" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {strategySections.map((section, index) => (
            <StrategySection key={index} title={section.title} description={section.description} processes={section.processes} outcomes={section.outcomes} timeframe={section.timeframe} />
          ))}
        </div>
        <Separator className="my-12 bg-white/10" />
        <PerformanceShowcase />
        <div className="mt-6">
          <h3 className="text-2xl font-bold mb-6">What makes this different</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h4 className="text-lg font-semibold mb-3 text-muted-foreground">Instead of</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2"><span className="text-red-400">✕</span> Static reporting and dashboards</li>
                <li className="flex items-start gap-2"><span className="text-red-400">✕</span> Retrospective performance reviews</li>
                <li className="flex items-start gap-2"><span className="text-red-400">✕</span> Manual KPI tracking</li>
                <li className="flex items-start gap-2"><span className="text-red-400">✕</span> Fragmented governance processes</li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-3 text-accent">You get</h4>
              <ul className="space-y-2 text-foreground">
                <li className="flex items-start"><span className="mr-2 text-accent">✓</span>Real-time, data-driven performance systems</li>
                <li className="flex items-start"><span className="mr-2 text-accent">✓</span>Integrated governance and decision frameworks</li>
                <li className="flex items-start"><span className="mr-2 text-accent">✓</span>Smart contract-enabled performance accountability</li>
                <li className="flex items-start"><span className="mr-2 text-accent">✓</span>Continuous feedback and improvement loops</li>
              </ul>
            </div>
          </div>

          <blockquote className="border-l-4 border-accent pl-6 my-8">
            <p className="text-xl font-medium italic text-foreground">
              "We turn performance into a managed, data-driven system."
            </p>
          </blockquote>
          <p className="text-muted-foreground mb-6">
            We make performance measurable, transparent, and continuously optimized through integrated systems, governance, and smart contract infrastructure.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20">
            <Brain className="h-4 w-4 text-accent" />
            <span className="text-sm font-medium text-accent">&nbsp;Powered by Contract Studio™</span>
          </div>
        </div>
      </ServiceLayout>
      <Footer />
    </>
  );
};

export default AIAssistants;
